import React, { useEffect } from "react";
import { dropDown } from "../../asset";
import DownloadButton from "../../components/common/DownloadButton";
import { useParams, useSearchParams } from "react-router-dom";
import { StudentMonthlyAttendance } from "../../types/attendanceType";
import attendanceApis from "../../api/attendanceApis";
import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import AttendanceHeader from "../staff-attendance-report/AttendanceHeader";
import { useBranchCode } from "../../util/hooks/useBranch";
import { getStudents } from "../../features/student/StudentSlice";
import {
  getStatusStyles,
  MONTHS,
  STATUS_DISPLAY_MAP,
} from "../../util/Constants";
import { exportToExcel } from "../../util/functions/UniversalFunctions";

export default function StudentAttendanceData() {
  const [searchParams] = useSearchParams();
  const classIdParam = Number(searchParams.get("classId"));

  const [attendanceMap, setAttendanceMap] = React.useState<
    Record<string, StudentMonthlyAttendance[]>
  >({});
  const [loading, setLoading] = React.useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { branchCode } = useParams<{ branchCode: string }>();

  const { students } = useSelector((state: RootState) => state.student);

  useEffect(() => {
    if (!classIdParam || !branchCode) return;

    dispatch(
      getStudents({
        branchCode,
        classId: classIdParam,
        page: 0,
        size: 1000,
        studentName: "",
      }),
    );
  }, [dispatch, classIdParam, branchCode]);

  const yearParam = Number(searchParams.get("year"));
  const monthParam = searchParams.get("month");

  const initialMonthIndex = MONTHS.indexOf(monthParam ?? "JANUARY");

  const [current, setCurrent] = React.useState(
    new Date(yearParam, initialMonthIndex, 1),
  );

  const year = current.getFullYear();
  const monthIndex = current.getMonth();
  const monthName = MONTHS[monthIndex];

  const monthLabel = current.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const prevMonth = () =>
    setCurrent((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));

  const nextMonth = () =>
    setCurrent((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

  const days = Array.from({ length: daysInMonth }, (_, i) => {
    const dateObj = new Date(year, monthIndex, i + 1);

    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");

    return {
      date: dateObj.toLocaleString("default", {
        month: "short",
        day: "2-digit",
      }),
      day: dateObj.toLocaleString("default", { weekday: "short" }),
      fullDate: `${yyyy}-${mm}-${dd}`,
    };
  });

  const attendanceByStudent: Record<string, Record<string, string>> = {};

  Object.entries(attendanceMap).forEach(([date, records]) => {
    records.forEach((r) => {
      const empKey = r.admissionNumber;
      if (!attendanceByStudent[empKey]) {
        attendanceByStudent[empKey] = {};
      }
      attendanceByStudent[empKey][date] = r.status ?? "";
    });
  });

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      const res = await attendanceApis.getStudentMonthlyAttendance(
        // branchCode,
        classIdParam,
        monthName,
        String(year),
      );

      if (res?.data) {
        setAttendanceMap(res.data);
      }
      setLoading(false);
    };

    fetchAttendance();
  }, [current, branchCode, classIdParam]);

  const buildExportData = () => {
    return students?.map((stu) => {
      const row: Record<string, string> = {
        "Roll No": String(stu.rollNo),
        "Student Name": stu.fullName,
      };

      const empAttendance = attendanceByStudent[stu.admissionNumber] || {};

      days.forEach((day) => {
        const status = empAttendance[day.fullDate];

        row[day.fullDate] = STATUS_DISPLAY_MAP[status];
      });

      return row;
    });
  };

  return (
    <div className="px-5 py-6 min-h-screen flex flex-col gap-[14px]">
      <AttendanceHeader />

      <div className="flex flex-col gap-4">
        <div className="relative flex items-center">
          <h3 className="text-[28px] text-neutral-400 font-semibold">
            MONTHLY ATTENDANCE REPORT
          </h3>

          <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
            <button onClick={prevMonth}>
              <img src={dropDown} alt="" className="rotate-90 w-4 h-4" />
            </button>

            <h2 className="text-2xl font-medium text-neutral-500 text-center min-w-[140px]">
              {monthLabel}
            </h2>

            <button onClick={nextMonth}>
              <img src={dropDown} alt="" className="-rotate-90 w-4 h-4" />
            </button>
          </div>

          <div className="ml-auto">
            <DownloadButton
              onClick={() => {
                const data = buildExportData();
                exportToExcel(data, `Student_Attendance_${monthName}_${year}`);
              }}
            ></DownloadButton>
          </div>
        </div>

        <div className="overflow-x-auto custom-scrollbar rounded-lg border border-neutral-50 shadow-sm bg-white">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-background-200 border-b border-neutral-50">
                <th className="p-4 text-sm font-semibold text-neutral-400 min-w-[250px] sticky left-0 bg-background-200">
                  Students
                </th>
                {days.map((d, i) => (
                  <th
                    key={i}
                    className="p-2 text-center border-l border-neutral-50 min-w-[70px]"
                  >
                    <div className="text-sm font-medium text-neutral-500">
                      {d.date}
                    </div>
                    <div className="text-[13px] text-neutral-300 font-medium">
                      {d.day}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {students?.map((emp, idx) => (
                <tr
                  key={idx}
                  className="border-b border-neutral-50 hover:bg-neutral-50 transition-colors"
                >
                  <td className="p-3 flex items-center gap-3 sticky left-0 bg-white z-50 border-r">
                    {/* <img
                      src={emp.employeeCode}
                      alt=""
                      className="w-8 h-8 rounded-full border shadow-sm"
                    /> */}
                    <div>
                      {!emp?.photoUrl ? (
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
                          {emp.fullName.charAt(0).toUpperCase()}
                        </div>
                      ) : (
                        <img
                          src={emp?.photoUrl}
                          alt="profile"
                          className="w-10 h-10 rounded-full object-cover bg-background-200"
                        />
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[13px] text-neutral-400 tracking-tighter uppercase">
                        {emp.rollNo}
                      </span>
                      <span className="text-[13px] font-semibold text-neutral-400 truncate w-32">
                        {emp.fullName}
                      </span>
                    </div>
                  </td>
                  {days.map((day, i) => {
                    const empAttendance =
                      attendanceByStudent[emp.admissionNumber] || {};
                    const status = empAttendance[day.fullDate] || "";

                    const cellDate = new Date(day.fullDate);
                    cellDate.setHours(0, 0, 0, 0);

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const displayStatus = STATUS_DISPLAY_MAP[status]
                      ? STATUS_DISPLAY_MAP[status]
                      : cellDate < today
                        ? "-"
                        : "";

                    const underlineClass =
                      "bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-[length:50%_3px] bg-[position:50%_80%]";

                    return (
                      <td
                        key={i}
                        className={`p-2 text-center text-sm font-semibold border-l border-neutral-50
        ${displayStatus && displayStatus !== "-" ? underlineClass : ""}
        ${getStatusStyles(status)}
      `}
                      >
                        {displayStatus}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
